/**
 * Maps RAW audit `changedFields` keys → friendly, human-readable labels.
 *
 * The profiles-api records raw field keys (e.g. "theFirm", "additionalInformation",
 * "staffing"). Anything not explicitly mapped falls back to `humanizeFieldKey`,
 * which converts snake_case / camelCase into sentence case so nothing ever
 * renders as a bare key.
 */
const FIELD_LABELS: Record<string, string> = {
  // Shared / common
  status: "Status",
  website: "Website",
  profileLanguage: "Profile language",
  languageCode: "Language",
  regions: "Countries",
  title: "Title",
  // Firm profile
  theFirm: "About the firm",
  about: "About the firm",
  additionalInformation: "Additional information",
  staffing: "Staffing",
  memberships: "Memberships",
  languages: "Languages",
  logoHash: "Logo",
  logoFilename: "Logo",
  source: "Source",
  profileType: "Profile type",
  // Lawyer profile
  firstName: "First name",
  lastName: "Last name",
  position: "Position",
  profession: "Profession",
  workDepartment: "Work department",
  telephone: "Telephone",
  email: "Email",
  photoHash: "Photo",
  photoFilename: "Photo",
  career: "Career",
  education: "Education",
  personal: "Personal",
  leisure: "Leisure",
  notableBarristers: "Notable barristers",
  currentBarristers: "Current barristers",
  legal500Quotes: "Legal 500 quotes",
  socialMedia: "Social media",
  languagesSpoken: "Languages spoken",
  // Article
  content: "Content",
  articleType: "Article type",
  featuredImageHash: "Featured image",
  featuredImageFilename: "Featured image",
  editorialCountryCode: "Editorial country",
  editorialCategories: "Editorial categories",
  contactId: "Linked lawyer",
  // Firm tab / team page
  tabType: "Tab type",
  teamName: "Team name",
  pageType: "Page type",
  companyId: "Company",
};

/**
 * Humanize an unmapped key: snake_case / kebab-case / camelCase → sentence case
 * ("someUnknownField" → "Some unknown field"). Only the first character is
 * capitalised — the rest is lowercased, so it reads as prose next to the mapped
 * labels above, which are sentence case too.
 */
export function humanizeFieldKey(key: string): string {
  const spaced = key
    // camelCase / PascalCase → space-separated
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    // snake_case / kebab-case → space-separated
    .replace(/[_-]+/g, " ")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? humanizeFieldKey(key);
}

export function fieldLabels(keys: readonly string[] | null | undefined): string[] {
  if (!keys) return [];
  return keys.map(fieldLabel);
}
